/**
 * Classify a voucher as Rajput (branch sales) or Rajdoot (labor/vendor).
 *
 * Accepts either:
 *   - A voucher/invoice object — uses `isVendorInvoice` (camelCase from snakeToCamel)
 *     or `is_vendor_invoice` as the authoritative signal.
 *   - A plain firm-name string — falls back to exact match against "Rajput Electricals"
 *     for backward compatibility (e.g. old cached rows without the boolean flag).
 *
 * @param {object|string} firmOrVoucher
 * @returns {'rajput'|'rajdoot'|'unknown'}
 */
const RAJPUT_FIRM = "rajput electricals";

export function classifyFirm(firmOrVoucher = "") {
  if (typeof firmOrVoucher === "object" && firmOrVoucher !== null) {
    const isVendor =
      firmOrVoucher.isVendorInvoice !== undefined
        ? firmOrVoucher.isVendorInvoice
        : firmOrVoucher.is_vendor_invoice;
    if (isVendor !== undefined) {
      return isVendor ? "rajdoot" : "rajput";
    }
    firmOrVoucher = firmOrVoucher.firm || "";
  }
  const name = String(firmOrVoucher || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
  if (!name) return "unknown";
  if (name === RAJPUT_FIRM) return "rajput";
  return "rajdoot";
}

/**
 * Pick Rajput / Rajdoot invoice from a list of related SR invoices.
 * Unknown firms (empty firm) are assigned using preferredFirm, then fill
 * whichever side is empty — defaulting to Rajdoot (labor) first, since
 * vendor invoices are the common case when firm text is missing.
 */
export function splitServiceRequestInvoices(invoices = [], preferredFirm = "") {
  const list = (invoices || []).filter(
    inv => inv && inv.voucherType === "invoice"
  );

  let rajputInvoice =
    list.find(inv => classifyFirm(inv) === "rajput") || null;
  let rajdootInvoice =
    list.find(inv => classifyFirm(inv) === "rajdoot") || null;

  const unknowns = list.filter(inv => classifyFirm(inv) === "unknown");
  const preferred = classifyFirm(preferredFirm);

  unknowns.forEach(inv => {
    if (preferred === "rajput") {
      if (!rajputInvoice) rajputInvoice = inv;
      else if (!rajdootInvoice) rajdootInvoice = inv;
    } else {
      // prefer rajdoot / default (vendor labor bills)
      if (!rajdootInvoice) rajdootInvoice = inv;
      else if (!rajputInvoice) rajputInvoice = inv;
    }
  });

  return { rajputInvoice, rajdootInvoice };
}

export function isInvoicePaid(invoice) {
  if (!invoice) return false;
  return (parseFloat(invoice.balance) || 0) <= 0;
}
